import { useSessionStorage } from "../../../hooks";
import { DashboardLayout } from "@layouts/index";
import image from "../../../assets/images/profile-pic.png";
import {
  sendOTP,
  updateProfile,
  validateOTP,
  updateProfilePic,
} from "@services/krent.service";
import { useState, useEffect } from "react";
import { Modal } from "@shared/controls/Modal";
import ktoast from "@services/toast.service";
import SuccessCard from "@shared/cards/SuccessCard";
import { useNavigate } from "react-router-dom";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";

export const EditProfile = () => {
  const [user, setUser] = useSessionStorage("user", {});

  const navigate = useNavigate();

  const [firstname, setFirstname] = useState<string>("");
  const [lastname, setLastname] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [nationality, setNationality] = useState<string>(
    user?.nationality || "",
  );
  const [otherCountryName, setOtherCountryName] = useState<string>("");
  const [address, setAddress] = useState<string>(user?.address || "");

  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [profilePic, setProfilePic] = useState<any>(false);
  const [profilePicUrl, setProfilePicUrl] = useState<any>(false);
  const [otpError, setOtpError] = useState<boolean>(false);
  const [otpErrorMsg, setOtpErrorMsg] = useState<string>("");
  const [otpSuccessMsg, setOtpSuccessMsg] = useState<string>("");
  const [isVerifiying, setIsVerifiying] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [phoneError, setPhoneError] = useState<string>("");
  const [autoComplete, setAutoComplete] =
    useState<google.maps.places.Autocomplete | null>(null);

  useEffect(() => {
    if (user?.nationality) {
      if (user.nationality === "nigeria") {
        setNationality("nigeria");
      } else {
        setNationality("others");
        setOtherCountryName(user.nationality);
      }
    }
    if (user?.address) {
      setAddress(user.address);
    }
  }, [user]);

  const onLoad = (autocomplete: google.maps.places.Autocomplete) => {
    setAutoComplete(autocomplete);
  };

  const onPlaceChanged = () => {
    if (autoComplete !== null) {
      const place = autoComplete.getPlace();
      const address = place.formatted_address;

      if (address) {
        setAddress(address);
      }
    } else {
      console.log("Autocomplete is not loaded yet!");
    }
  };

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env["REACT_APP_GOOGLE_API_KEY"] || "",
    libraries: ["places"],
  });
  const handleChange = (e: any) => {
    const value = e.target.files;
    const [file] = value;
    setProfilePic(value);
    setProfilePicUrl(URL.createObjectURL(file));
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    const abortController = new AbortController();

    setLoading(true);

    if (profilePic) {
      const formData = new FormData();
      formData.append("photo", profilePic[0]);
      updateProfilePic(user?.id, formData, abortController.signal)
        .then((res: any) => {
          setUser(res.data);
        })
        .catch(() => {})
        .finally(() => {
          setLoading(false);
          setTimeout(() => {
            navigate("/profile");
          }, 1000);
        });
    }

    setLoading(true);

    if (
      firstname ||
      lastname ||
      phone ||
      nationality ||
      otherCountryName ||
      address
    ) {
      updateProfile(
        user?.id,
        {
          firstname: firstname || user.firstname,
          lastname: lastname || user.lastname,
          phone: phone || user.phone,
          nationality: otherCountryName || nationality || user.nationality,
          address: address || user.address,
        },
        abortController.signal,
      )
        .then((res: any) => {
          setUser(res.data);
        })
        .catch(() => {})
        .finally(() => {
          setLoading(false);
          setTimeout(() => {
            navigate("/profile");
          }, 1000);
        });
    }
  };

  const handleVerifyOTP = () => {
    setLoading(true);
    const code = Array.prototype.map
      .call(document.getElementsByClassName("digits"), (e) => e.value)
      .join("");
    let abortController = new AbortController();
    setIsVerifiying(true);
    validateOTP({ otp: code }, abortController.signal)
      .then(() => {
        setLoading(false);
        setOtpSuccessMsg("Phone number verified successfully.");
        ktoast.success("Phone number verified successfully.");
        setUser({ ...user, verified: [...user?.verified, "phone"] });
      })
      .catch((err) => {
        setOtpError(true);
        setLoading(false);
        ktoast.error(err.errors[0].message);
        setOtpErrorMsg(err.errors[0].message);
        setTimeout(() => {
          setOtpErrorMsg("");
        }, 3000);
      })
      .finally(() => {
        setIsVerifiying(false);
        setLoading(false);
      });
  };

  const verifyPhone = () => {
    let abortController = new AbortController();
    setLoading(true);
    sendOTP(abortController.signal)
      .then(() => {
        setModalOpen(true);
      })
      .catch((err) => {
        ktoast.error(err.errors[0].message);
        setLoading(false);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <DashboardLayout
      title="My Profile"
      subtitle=" Update your profile on Krent "
    >
      <div className="row">
        <div className="container">
          <div className="card border-0 border-round py-5 gap-3">
            <div className="mb-4 border-bottom pb-3">
              <div className="d-flex align-items-center gap-3">
                <i className="fa-regular fa-user fs-14 text-primary"></i>
                <h5 className="fw-500 font-onest">Personal Information</h5>
              </div>
            </div>
            <div className="card-body px-md-0 px-4 d-inline-block d-md-flex gap-5">
              <div className="row ">
                <div className="col-12 col-md-5 mb-5 mb-md-0 text-center">
                  <div
                    className="position-relative d-inline-block"
                    style={{ width: "150px", height: "150px" }}
                  >
                    <img
                      src={
                        profilePicUrl
                          ? profilePicUrl
                          : user.photo !== "default.jpg"
                            ? `${user?.photo}`
                            : image
                      }
                      alt={user ? user?.preferredName : "Avatar"}
                      style={{ objectFit: "cover" }}
                      height={150}
                      width={150}
                      className="rounded-circle "
                    />
                    <input
                      type="file"
                      id="upload-circle"
                      hidden
                      name="profilePic"
                      onChange={handleChange}
                    />

                    <label
                      htmlFor="upload-circle"
                      className="rounded-circle d-flex align-items-center justify-content-center bg-dark opacity-75 position-absolute top-0 start-0 cursor-pointer"
                      style={{ height: 150, width: 150 }}
                    >
                      <i className="fa-light fa-camera fs-22 text-white"></i>
                    </label>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6 col-12 mb-5">
                    <label className="fw-bolder form-label text-capitalize">
                      First name
                    </label>
                    <input
                      type="text"
                      className="form-control rounded-pill py-3 px-4 fs-14"
                      defaultValue={firstname ? firstname : user?.firstname}
                      onChange={(e) => {
                        setFirstname(e.target.value);
                      }}
                      disabled
                    />
                  </div>
                  <div className="col-md-6 col-12 mb-5">
                    <label className="fw-bolder form-label text-capitalize">
                      Last name
                    </label>
                    <input
                      type="text"
                      className="form-control rounded-pill py-3 px-4 fs-14"
                      defaultValue={lastname ? lastname : user?.lastname}
                      onChange={(e) => {
                        setLastname(e.target.value);
                      }}
                      disabled
                    />
                  </div>
                  <div className="col-md-6 col-12 mb-5">
                    <label className="fw-bolder form-label text-capitalize">
                      Preferred name
                    </label>
                    <input
                      type="text"
                      className="form-control rounded-pill py-3 px-4 fs-14"
                      defaultValue={user?.preferredName}
                      disabled
                    />
                    <span className="fs-10 ml-3 text-danger">
                      You can not edit your preferred Name
                    </span>
                  </div>
                  {/* <div className='col-md-6 col-12 mb-5'>
                    <label className='fw-bolder form-label text-capitalize'>
                      Gender
                    </label>
                    <select className='form-select rounded-pill py-3 px-4 fs-14'>
                      <option value='Male'>Male</option>
                      <option value='Female'>Female</option>
                    </select>
                  </div> */}
                  <div className="col-md-6 col-12 mb-5">
                    <label className="fw-bolder form-label text-capitalize">
                      Nationality
                    </label>
                    <select
                      className="form-select rounded-pill py-3 px-4 fs-14"
                      value={nationality}
                      onChange={(e) => {
                        setNationality(e.target.value);
                      }}
                    >
                      <option>Select Country</option>
                      <option value="nigeria">Nigerian</option>
                      <option value="others">Others</option>
                    </select>
                    {nationality === "others" && (
                      <input
                        type="text"
                        className="form-control mt-3"
                        placeholder="Enter Country Name"
                        value={otherCountryName}
                        onChange={(e) => {
                          setOtherCountryName(e.target.value);
                        }}
                      />
                    )}
                  </div>
                  <div className="col-md-6 col-12 mb-5">
                    <label className="fw-bolder form-label text-capitalize">
                      Address
                    </label>
                    {isLoaded ? (
                      <Autocomplete
                        onLoad={onLoad}
                        onPlaceChanged={onPlaceChanged}
                      >
                        <input
                          type="text"
                          className="form-control rounded-pill py-3 px-4 fs-14 mb-3"
                          value={address}
                          onChange={(e) => {
                            setAddress(e.target.value);
                          }}
                          required
                          name="address"
                          placeholder="Input your address"
                        />
                      </Autocomplete>
                    ) : (
                      <input
                        type="text"
                        className="form-control rounded-pill py-3 px-4 fs-14 mb-3"
                        value={address}
                        onChange={(e) => {
                          setAddress(e.target.value);
                        }}
                        required
                        name="address"
                        placeholder="Input your address"
                      />
                    )}
                  </div>
                  <div className="col-md-6 col-12 mb-5">
                    <label className="fw-bolder form-label text-capitalize">
                      Phone number
                    </label>
                    <input
                      type="text"
                      className={`form-control rounded-pill py-3 px-4 fs-14 ${phoneError ? "is-invalid" : ""}`}
                      defaultValue={phone ? phone : user?.phone}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only allow numbers
                        const numbersOnly = value.replace(/\D/g, "");
                        // Limit to 13 digits
                        const limitedValue = numbersOnly.slice(0, 13);
                        setPhone(limitedValue);

                        // Validate phone number
                        if (
                          limitedValue.length > 0 &&
                          (limitedValue.length < 10 || limitedValue.length > 13)
                        ) {
                          setPhoneError(
                            "Phone number must be between 10 and 13 digits",
                          );
                        } else {
                          setPhoneError("");
                        }
                      }}
                      maxLength={13}
                    />
                    {phoneError && (
                      <div className="invalid-feedback d-block">
                        {phoneError}
                      </div>
                    )}
                  </div>
                </div>
                <div className="row">
                  <div>
                    <button
                      type="submit"
                      className="btn btn-primary rounded-pill py-2 px-4 fw-medium fs-14"
                      disabled={loading || !!phoneError}
                    >
                      {loading ? (
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        ></span>
                      ) : (
                        "Save Preferences"
                      )}
                    </button>
                  </div>
                </div>
                {/* </div>  */}
              </form>
            </div>
          </div>
        </div>
      </div>
      <Modal
        show={isModalOpen}
        onClose={() => {
          setModalOpen(false);
          setOtpSuccessMsg("");
        }}
      >
        <div className="container height-100 d-flex justify-content-center align-items-center">
          {otpSuccessMsg ? (
            <SuccessCard text={otpSuccessMsg} />
          ) : (
            <div className="position-relative">
              <div className="px-2 text-center">
                <p className="fs-16">
                  Please enter the one time password to verify your account
                </p>
                {otpErrorMsg ? (
                  <p className="text-danger">{otpErrorMsg}</p>
                ) : otpSuccessMsg ? (
                  <p className="text-success">{otpSuccessMsg}</p>
                ) : (
                  <p className="fs-14">
                    A code has been sent to{" "}
                    <span className="fw-bold">
                      *******{user?.phone.slice(-4)}
                    </span>
                  </p>
                )}

                <div
                  id="otp"
                  className={`inputs d-flex flex-row justify-content-center mt-2${
                    otpError ? " invalid-otp" : ""
                  }`}
                >
                  <input
                    className="m-2 py-3 px-3 text-center form-control rounded digits"
                    type="text"
                    id="first"
                    maxLength={1}
                    onFocus={() => setOtpError(false)}
                    onChange={(e) => {
                      if (e.target.value) {
                        document!.getElementById("second")!.focus();
                      }
                    }}
                  />
                  <input
                    className="m-2 py-3 px-3 text-center form-control rounded digits"
                    type="text"
                    id="second"
                    maxLength={1}
                    onChange={(e) => {
                      if (e.target.value) {
                        document!.getElementById("third")!.focus();
                      }
                    }}
                  />
                  <input
                    className="m-2 py-3 px-3 text-center form-control rounded digits"
                    type="text"
                    id="third"
                    maxLength={1}
                    onChange={(e) => {
                      if (e.target.value) {
                        document!.getElementById("fourth")!.focus();
                      }
                    }}
                  />
                  <input
                    className="m-2 py-3 px-3 text-center form-control rounded digits"
                    type="text"
                    id="fourth"
                    maxLength={1}
                    onChange={(e) => {
                      if (e.target.value) {
                        document!.getElementById("fifth")!.focus();
                      }
                    }}
                  />
                  <input
                    className="m-2 py-3 px-3 text-center form-control rounded digits"
                    type="text"
                    id="fifth"
                    maxLength={1}
                    onChange={(e) => {
                      if (e.target.value) {
                        document!.getElementById("sixth")!.focus();
                      }
                    }}
                  />
                  <input
                    className="m-2 py-4 px-3 text-center form-control rounded digits"
                    type="text"
                    id="sixth"
                    maxLength={1}
                  />
                </div>
                <div className="mt-4">
                  <button
                    className="btn btn-primary rounded-pill px-5 py-2"
                    onClick={handleVerifyOTP}
                  >
                    Validate
                  </button>
                </div>
              </div>
              <div className="mt-3 text-center">
                <div className="content d-flex justify-content-center align-items-center">
                  <button
                    disabled={isVerifiying}
                    className="btn btn-link fs-14 text-decoration-none"
                  >
                    Didn't get the code?{" "}
                    <span className="text-dark" onClick={verifyPhone}>
                      Resend
                    </span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </DashboardLayout>
  );
};
